/**
 * MarkdownTable Component
 *
 * Renders a markdown table from raw markdown table syntax.
 * Parses simple pipe-delimited table format and renders as HTML table.
 *
 * Supports:
 * - Header row
 * - Alignment row (|---|---|)
 * - Data rows
 * - Inline fact tokens (^fid=xxx^) rendered as fact badges
 */

import React, { useMemo } from 'react';
import InlineFactBadge from './InlineFactBadge';
import { useAppSelector } from '../hooks/reduxHooks';
import { selectAllFacts } from '../ResultsSlice_Store';

interface MarkdownTableProps {
  content: string;
  fadeIn?: boolean;
}

interface TableData {
  headers: string[];
  alignments: ('left' | 'center' | 'right')[];
  rows: string[][];
}

/**
 * Split a pipe-delimited table row into cells.
 * Strips the leading and trailing empty strings caused by outer pipes
 * (e.g. "| a | b |" → ["", " a ", " b ", ""] → [" a ", " b "])
 * but preserves genuinely empty middle cells.
 */
function splitTableRow(line: string): string[] {
  const parts = line.split('|').map(cell => cell.trim());
  // Strip leading empty entry (before first |)
  if (parts.length > 0 && parts[0] === '') {
    parts.shift();
  }
  // Strip trailing empty entry (after last |)
  if (parts.length > 0 && parts[parts.length - 1] === '') {
    parts.pop();
  }
  return parts;
}

/**
 * Parse markdown table syntax into structured data
 */
function parseMarkdownTable(markdown: string): TableData | null {
  const lines = markdown.trim().split('\n').filter(line => line.trim());

  if (lines.length < 2) {
    return null; // Need at least header and alignment row
  }

  // Parse header row
  const headerLine = lines[0];
  const headers = splitTableRow(headerLine);

  if (headers.length === 0) {
    return null;
  }

  // Parse alignment row (e.g., |---|:---:|---:|)
  const alignmentLine = lines[1];
  const alignmentCells = splitTableRow(alignmentLine);

  const alignments: ('left' | 'center' | 'right')[] = alignmentCells.map(cell => {
    if (cell.startsWith(':') && cell.endsWith(':')) {
      return 'center';
    } else if (cell.endsWith(':')) {
      return 'right';
    } else {
      return 'left';
    }
  });

  // Parse data rows
  const rows: string[][] = [];
  for (let i = 2; i < lines.length; i++) {
    const line = lines[i];
    const cells = splitTableRow(line);

    if (cells.length > 0) {
      rows.push(cells);
    }
  }

  return { headers, alignments, rows };
}

/**
 * Parse and render cell content, handling:
 * - ^fid=xxx^ tokens → fact badges (streaming)
 * - [[FACT:xxx]] placeholders → fact badges (reloaded/historical)
 * - **text** → bold text
 * - *text* → italic text
 */
function CellContent({ text, allFacts, getFactDisplayIndex }: {
  text: string;
  allFacts: any[];
  getFactDisplayIndex: (factId: string) => number | null;
}) {
  // Combined pattern to match facts (both formats), bold text, and italic text
  // Group 1: fact ID from ^fid=xxx^ (streaming)
  // Group 2: fact ID from [[FACT:xxx]] (reloaded)
  // Group 3: bold text from **text** (full match)
  // Group 4: bold text content
  // Group 5: italic text from *text* (full match)
  // Group 6: italic text content
  const pattern = /\^fid=([^^\s]+)\^|\[\[FACT:([^\]]+)\]\]|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)/g;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let keyIndex = 0;

  while ((match = pattern.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    if (match[1] || match[2]) {
      // Fact token: ^fid=xxx^ (group 1) or [[FACT:xxx]] (group 2)
      const factId = match[1] || match[2];
      const fact = allFacts.find((f: any) => f.factId === factId);
      const displayIndex = getFactDisplayIndex(factId);

      parts.push(
        <InlineFactBadge
          key={`fact-${keyIndex++}`}
          factId={factId}
          fact={fact}
          displayIndex={displayIndex}
          documentDisplayIndex={null}
          allFacts={allFacts}
          loading={false}
        />
      );
    } else if (match[3]) {
      // Bold text: **text**
      parts.push(
        <strong key={`bold-${keyIndex++}`} className="font-semibold">
          {match[4]}
        </strong>
      );
    } else if (match[5]) {
      // Italic text: *text*
      parts.push(
        <em key={`italic-${keyIndex++}`}>
          {match[6]}
        </em>
      );
    }

    lastIndex = pattern.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  // If no special content found, return plain text
  if (parts.length === 0) {
    return <>{text}</>;
  }

  return <>{parts}</>;
}

export default function MarkdownTable({ content, fadeIn = false }: MarkdownTableProps) {
  const tableData = useMemo(() => parseMarkdownTable(content), [content]);
  const allFacts = useAppSelector(selectAllFacts);

  // Build a map of factId -> displayIndex (1-based sequential number)
  const factDisplayIndexMap = useMemo(() => {
    const map: Record<string, number> = {};
    allFacts.forEach((fact: any, index: number) => {
      map[fact.factId] = index + 1;
    });
    return map;
  }, [allFacts]);

  // Helper to get display index for a fact
  const getFactDisplayIndex = (factId: string): number | null => {
    return factDisplayIndexMap[factId] || null;
  };

  if (!tableData) {
    // Fallback: show raw content if parsing fails
    return <pre className="my-4 overflow-x-auto rounded bg-gray-50 p-4 text-sm">{content}</pre>;
  }

  const { headers, alignments, rows } = tableData;

  return (
    <div
      className={`my-2 overflow-hidden rounded-lg border border-gray-200 bg-white ${
        fadeIn ? 'animate-fadeIn' : ''
      }`}
      style={{
        animation: fadeIn ? 'fadeIn 0.5s ease-in' : undefined
      }}
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {headers.map((header, index) => {
                const alignment = alignments[index] || 'left';
                const alignClass =
                  alignment === 'center'
                    ? 'text-center'
                    : alignment === 'right'
                    ? 'text-right'
                    : 'text-left';

                return (
                  <th
                    key={`header-${index}`}
                    className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-700 ${alignClass}`}
                  >
                    {header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {rows.map((row, rowIndex) => (
              <tr key={`row-${rowIndex}`} className="hover:bg-gray-50">
                {row.map((cell, cellIndex) => {
                  const alignment = alignments[cellIndex] || 'left';
                  const alignClass =
                    alignment === 'center'
                      ? 'text-center'
                      : alignment === 'right'
                      ? 'text-right'
                      : 'text-left';

                  return (
                    <td
                      key={`cell-${rowIndex}-${cellIndex}`}
                      className={`whitespace-normal px-6 py-4 text-sm text-gray-900 ${alignClass}`}
                    >
                      <CellContent text={cell} allFacts={allFacts} getFactDisplayIndex={getFactDisplayIndex} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add fadeIn animation styles */}
      <style>{`
        @keyframes fadeIn {
          0% {
            opacity: 0;
            transform: translateY(-5px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
